import clsx from 'clsx';
import React from 'react';

import ResetIcon from '@/assets/icons/reload.svg';

import { Button } from './button';
import { Skeleton } from './skeleton';
import { Tooltip } from './tooltip';
import { Typography } from './typography';

interface InputProps {
  label?: string;
  placeholderIcon?: string;
  placeholderLabel?: string;
  error?: string;
  isNumber?: boolean;
  onReset?: () => void;
  resetTooltipContent?: string;
  isFetching?: boolean;
}

export const Input = React.forwardRef<
  HTMLInputElement,
  InputProps & React.ComponentProps<'input'>
>(
  (
    {
      label,
      placeholderIcon,
      placeholderLabel,
      error,
      className,
      isNumber,
      onReset,
      resetTooltipContent,
      isFetching,
      ...props
    },
    ref,
  ) => {
    // Function to validate number input
    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
      if (isNumber) {
        const input = e.currentTarget;
        let value = input.value
          .replace(/[^0-9.]/g, '') // allow only numbers and one dot
          .replace(/^([0-9]*\.?[0-9]*).*$/, '$1');

        const num = parseFloat(value);
        if (typeof props.max === 'number' && num > props.max) {
          value = props.max.toString();
        } else if (typeof props.min === 'number' && num < props.min) {
          value = props.min.toString();
        }

        // If the value starts with a dot, prepend a zero
        if (value.startsWith('.')) {
          value = `0${value}`;
        }
        input.value = value;
      }
    };

    return (
      <div className={clsx('flex w-full flex-col gap-1', className)}>
        {label && (
          <label htmlFor={props.id}>
            <Typography variant="subtitle2">{label}</Typography>
          </label>
        )}

        <div className="relative">
          {(!!placeholderIcon || !!placeholderLabel) && (
            <div className="text-fx-neutral-400 pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
              {!!placeholderIcon && <img src={placeholderIcon} alt="Icon" />}
              {!!placeholderLabel && (
                <Typography variant="inactive1" className="w-4 text-center">
                  {placeholderLabel}
                </Typography>
              )}
              {isFetching && <Skeleton className="h-4 w-12" />}
            </div>
          )}

          <input
            ref={ref}
            data-slot="input"
            className={clsx(
              'placeholder:text-fx-neutral-600 flex h-9 w-full min-w-0 border text-base outline-none transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
              'border-fx-neutral-300  text-fx-neutral-900 rounded-sm bg-white px-2 py-1.5',
              { 'pl-8': !!placeholderIcon || !!placeholderLabel },
              { '!border-fx-rose': !!error },
              {
                'focus:border-fx-brand-600 focus:shadow-selected focus:border focus:outline-none':
                  !props.readOnly,
              },
            )}
            onInput={handleInput}
            {...props}
            placeholder={isFetching ? '' : props.placeholder}
          />

          {!!onReset && (
            <Tooltip content={resetTooltipContent}>
              <Button
                variant="icon"
                size="icon"
                onClick={onReset}
                className="absolute right-0 top-0"
              >
                <img src={ResetIcon} alt="Reset" />
              </Button>
            </Tooltip>
          )}
        </div>

        {error && (
          <Typography variant="small1" className="!text-fx-rose">
            {error}
          </Typography>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
